import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import { Add, Delete, Edit, ViewModule } from '@mui/icons-material';
import { getImageDisplayUrl } from '../../../config/api';

const TOPIC_LABELS = {
  'offers': 'Offers',
};

export default function TopicsTab({
  topics = [],
  places = [],
  onAddFeatured,
  onUpdateFeatured,
  onRemoveFeatured,
  loading,
  getHeaders,
  token,
}) {
  const [addDialog, setAddDialog] = useState({ open: false, topicKey: null });
  const [editDialog, setEditDialog] = useState({ open: false, topicKey: null, placeId: null, order: 0, featuredDescription: '' });
  const [addPlaceId, setAddPlaceId] = useState('');
  const [addOrder, setAddOrder] = useState(0);
  const [addFeaturedDescription, setAddFeaturedDescription] = useState('');

  const handleOpenAdd = (topicKey) => {
    setAddPlaceId('');
    setAddOrder(topics.find((t) => t.topicKey === topicKey)?.featured?.length || 0);
    setAddFeaturedDescription('');
    setAddDialog({ open: true, topicKey });
  };

  const handleAdd = async () => {
    if (!addDialog.topicKey || !addPlaceId) return;
    if (!addFeaturedDescription?.trim()) return;
    await onAddFeatured(addDialog.topicKey, { placeId: addPlaceId, order: addOrder, featuredDescription: addFeaturedDescription.trim() });
    setAddDialog({ open: false, topicKey: null });
  };

  const handleOpenEdit = (topicKey, item) => {
    setEditDialog({
      open: true,
      topicKey,
      placeId: item.placeId,
      order: item.order ?? 0,
      featuredDescription: item.featuredDescription || '',
    });
  };

  const handleEdit = async () => {
    if (!editDialog.topicKey || !editDialog.placeId) return;
    await onUpdateFeatured(editDialog.topicKey, editDialog.placeId, { order: editDialog.order, featuredDescription: editDialog.featuredDescription });
    setEditDialog({ open: false, topicKey: null, placeId: null, order: 0, featuredDescription: '' });
  };

  const dateDayStart = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  };

  const hasActiveOffer = (p) => {
    const today = dateDayStart(new Date());
    if (!today) return false;
    return (p.offers || []).some((o) => {
      if (!o.startDate || !o.expiryDate) return false;
      const startDate = dateDayStart(o.startDate);
      const expiryDate = dateDayStart(o.expiryDate);
      if (!startDate || !expiryDate) return false;
      return startDate <= today && expiryDate >= today;
    });
  };
  const offersTopic = (topics || []).find((t) => t.topicKey === 'offers');
  const featuredIds = new Set(
    (offersTopic?.featured || []).map((f) => String(f.placeId)),
  );
  const placesWithOffers = (places || [])
    .filter(hasActiveOffer)
    .filter((p) => !featuredIds.has(String(p._id)));

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 3 }}>
        Topics – Featured by section
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage manual featured places for each home section. If no manual entries, the app uses autofill only. Max slots per topic apply.
      </Typography>
      {loading ? (
        <Typography color="text.secondary">Loading topics…</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {(topics || []).filter((topic) => topic.topicKey === 'offers').map((topic) => {
            const featured = topic.featured || [];
            const atMax = featured.length >= (topic.maxFeatured || 6);
            return (
              <Card key={topic.topicKey} sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)', overflow: 'hidden' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ViewModule color="primary" />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {TOPIC_LABELS[topic.topicKey] || topic.title || topic.topicKey}
                      </Typography>
                      <Chip label={`${featured.length} / ${topic.maxFeatured || 6}`} size="small" color="primary" variant="outlined" />
                    </Box>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<Add />}
                      disabled={atMax}
                      onClick={() => handleOpenAdd(topic.topicKey)}
                      sx={{
                        background: atMax ? undefined : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        textTransform: 'none',
                      }}
                    >
                      Add place
                    </Button>
                  </Box>
                  {featured.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No manual featured places. App will show autofill for this section.
                    </Typography>
                  ) : (
                    <List dense>
                      {featured
                        .slice()
                        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                        .map((item) => (
                          <ListItem key={String(item.placeId)} divider sx={{ borderRadius: 1, mb: 0.5 }}>
                            <ListItemAvatar>
                              {item.place?.images?.[0] ? (
                                <Box
                                  component="img"
                                  src={getImageDisplayUrl(item.place.images[0])}
                                  alt=""
                                  sx={{ width: 48, height: 48, borderRadius: 1, objectFit: 'cover', bgcolor: 'action.hover' }}
                                  onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }}
                                />
                              ) : (
                                <Box sx={{ width: 48, height: 48, borderRadius: 1, bgcolor: 'action.hover' }} />
                              )}
                            </ListItemAvatar>
                            <ListItemText
                              primary={item.place?.name || item.placeId}
                              secondary={item.featuredDescription ? item.featuredDescription : null}
                            />
                            <ListItemSecondaryAction>
                              <IconButton size="small" onClick={() => handleOpenEdit(topic.topicKey, item)}>
                                <Edit />
                              </IconButton>
                              <IconButton size="small" color="error" onClick={() => onRemoveFeatured(topic.topicKey, item.placeId)}>
                                <Delete />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}

      <Dialog open={addDialog.open} onClose={() => setAddDialog({ open: false, topicKey: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Add featured place</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Place</InputLabel>
            <Select
              value={addPlaceId}
              onChange={(e) => setAddPlaceId(e.target.value)}
              label="Place"
            >
              {placesWithOffers.length === 0 ? (
                <MenuItem value="" disabled>
                  No places available. All places with active offers are already added.
                </MenuItem>
              ) : (
                placesWithOffers.map((p) => (
                  <MenuItem key={p._id} value={p._id}>
                    {p.name} {p.hasOffer && '(has offer)'}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            type="number"
            label="Order"
            value={addOrder}
            onChange={(e) => setAddOrder(parseInt(e.target.value, 10) || 0)}
            sx={{ mt: 2 }}
            inputProps={{ min: 0 }}
          />
          <TextField
            fullWidth
            label="Featured description (required for Offers)"
            value={addFeaturedDescription}
            onChange={(e) => setAddFeaturedDescription(e.target.value)}
            placeholder="e.g. Visit and enjoy offers from nezoola"
            multiline
            rows={2}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialog({ open: false, topicKey: null })}>Cancel</Button>
          <Button variant="contained" onClick={handleAdd} disabled={!addPlaceId || !addFeaturedDescription?.trim()}>
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, topicKey: null, placeId: null, order: 0, featuredDescription: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>Edit featured</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            type="number"
            label="Order"
            value={editDialog.order}
            onChange={(e) => setEditDialog((prev) => ({ ...prev, order: parseInt(e.target.value, 10) || 0 }))}
            sx={{ mt: 2 }}
            inputProps={{ min: 0 }}
          />
          <TextField
            fullWidth
            label="Featured description"
            value={editDialog.featuredDescription}
            onChange={(e) => setEditDialog((prev) => ({ ...prev, featuredDescription: e.target.value }))}
            multiline
            rows={2}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, topicKey: null, placeId: null, order: 0, featuredDescription: '' })}>Cancel</Button>
          <Button variant="contained" onClick={handleEdit}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
